import * as config from "../../constants/config";
import * as formulaTypes from "../../constants/formulaTypes";
import * as rcc from "../../constants/rcc";
import * as settings from "../../constants/settings";
import * as statsTypes from "../../constants/statsTypes";
import * as conversionHelper from "../../helpers/conversionHelper";
import * as formatHelper from "../../helpers/formatHelper";
import * as modelHelper from "../../helpers/modelHelper";
import * as translationHelper from "../../helpers/translationHelper";

import MachineColorant from "./MachineColorant";

/**
 * @class Formula
 */
export default class Formula {
    /**
     * @param {Number}  id
     * @param {Object}  color
     * @param {Array}   colorantList
     * @param {Object}  created
     */
    constructor(id, color, colorantList = [], created = null, updated = null) {
        this._id = id;
        this._color = color;
        this._scaling = 1;
        this._modified = false;
        this._colorantList = colorantList;
        this._colorantListG = [];
        this._colorantListShot = [];
        this._colorantListMachine = [];
        this._modifiedColorantList = [];
        this._modifiedColorantListG = [];
        this._modifiedColorantListShot = [];
        this._dispensedColorantList = [];
        this._dispensedColorantListG = [];
        this._dispensedColorantListShot = [];
        this._created = created;
        this._updated = updated;
        this._enabled = false;
        this._verified = false;
        this._rejected = false;
        this._notAvailable = false;
        this._statsType = null;
        this._cifId = null;
        this._bipId = null;
        this._colorantsGroupId = null;
        this._destinationId = null;
        this._validation = null;
        this._validationAll = null;
        this._formulaCombinationId = null;
        this._formulaSourceId = null;
        this._formulaTypeId = null;

        if (typeof this._id !== "number" && this._id !== null) {
            throw new Error("Invalid datatype of ID - Formula");
        }

        if (!this._color) {
            throw new Error("Invalid value of color");
        }
    }

    /**
     * @return {Number}
     */
    get id() {
        return this._id;
    }

    /**
     * @param {Number} id
     */
    set id(id) {
        this._id = id;
    }

    /**
     * @return {Number}
     */
    get formulaCombinationId() {
        return this._formulaCombinationId;
    }

    /**
     * @param {Number} formulaCombinationId
     */
    set formulaCombinationId(formulaCombinationId) {
        this._formulaCombinationId = formulaCombinationId;
    }

    /**
     * @return {Object}
     */
    get color() {
        return this._color;
    }

    /**
     * @param {Object} color
     */
    set color(color) {
        this._color = color;
    }

    /**
     * @return {String}
     */
    get name() {
        return this._color.name;
    }

    /**
     * @param {String} name
     */
    set name(name) {
        this._color.name = name;
    }

    /**
     * @return {String}
     */
    get displayName() {
        return this._color.displayName;
    }

    /**
     * @param {String} displayName
     */
    set displayName(displayName) {
        this._color.displayName = displayName;
    }

    /**
     * @return {String}
     */
    get code() {
        return this._color.code;
    }

    /**
     * @param {String} code
     */
    set code(code) {
        this._color.code = code;
    }

    /**
     * @return {String}
     */
    get rgb() {
        return this._color.rgb;
    }

    /**
     * @param {String} rgb
     */
    set rgb(rgb) {
        this._color.rgb = rgb;
    }

    /**
     * @return {Array}
     */
    get colorantList() {
        return this._colorantList;
    }

    /**
     * @return {Array}
     */
    get modifiedColorantList() {
        if (this._modified) {
            return this._modifiedColorantList;
        }

        return this._colorantList;
    }

    /**
     * @return {Array}
     */
    get realColorantList() {
        if (this._modified || this._scaling !== 1) {
            return this._modifiedColorantList;
        }

        return this._colorantList;
    }

    /**
     * @param {Array} colorantList
     */
    set colorantList(colorantList) {
        this._colorantList = colorantList;
    }

    /**
     * @return {Array}
     */
    get colorantListG() {
        return this._colorantListG;
    }

    /**
     * @return {Array}
     */
    get modifiedColorantListG() {
        if (this._modified) {
            return this._modifiedColorantListG;
        }

        return this._colorantListG;
    }

    /**
     * @return {Array}
     */
    get realColorantListG() {
        if (this._modified || this._scaling !== 1) {
            return this._modifiedColorantListG;
        }

        return this._colorantListG;
    }

    /**
     * @param {Array} colorantListG
     */
    set colorantListG(colorantListG) {
        this._colorantListG = colorantListG;
    }

    /**
     * @return {Array}
     */
    get colorantListShot() {
        return this._colorantListShot;
    }

    /**
     * @return {Array}
     */
    get modifiedColorantListShot() {
        if (this._modified) {
            return this._modifiedColorantListShot;
        }

        return this._colorantListShot;
    }

    /**
     * @return {Array}
     */
    get realColorantListShot() {
        if (this._modified || this._scaling !== 1) {
            return this._modifiedColorantListShot;
        }

        return this._colorantListShot;
    }

    /**
     * @param {Array} colorantListShot
     */
    set colorantListShot(colorantListShot) {
        this._colorantListShot = colorantListShot;
    }

    /**
     * @return {Array}
     */
    get colorantListMachine() {
        return this._colorantListMachine;
    }

    /**
     * @param {Array} colorantListMachine
     */
    set colorantListMachine(colorantListMachine) {
        this._colorantListMachine = colorantListMachine;
    }

    /**
     * @return {Array}
     */
    get dispensedColorantList() {
        return this._dispensedColorantList;
    }

    /**
     * @param {Array} colorantList
     */
    set dispensedColorantList(colorantList) {
        this._dispensedColorantList = colorantList;
    }

    /**
     * @return {Array}
     */
    get dispensedColorantListG() {
        return this._dispensedColorantListG;
    }

    /**
     * @param {Array} colorantList
     */
    set dispensedColorantListG(colorantList) {
        this._dispensedColorantListG = colorantList;
    }

    /**
     * @return {Array}
     */
    get dispensedColorantListShot() {
        return this._dispensedColorantListShot;
    }

    /**
     * @param {Array} colorantList
     */
    set dispensedColorantListShot(colorantList) {
        this._dispensedColorantListShot = colorantList;
    }

    /**
     * @return {Number}
     */
    get colorantsGroupId() {
        return this._colorantsGroupId;
    }

    /**
     * @param {Number} colorantsGroupId
     */
    set colorantsGroupId(colorantsGroupId) {
        this._colorantsGroupId = colorantsGroupId;
    }

    /**
     * @return {Number}
     */
    get destinationId() {
        return this._destinationId;
    }

    /**
     * @param {Number} destinationId
     */
    set destinationId(destinationId) {
        this._destinationId = destinationId;
    }

    /**
     * @return {Number | null}
     */
    get validation() {
        return this._validation;
    }

    /**
     * @return {Number | null}
     */
    get validationAll() {
        return this._validationAll;
    }

    /**
     * @return {Boolean}
     */
    get local() {
        return this._color.local;
    }

    /**
     * @param {Boolean} local
     */
    set local(local) {
        this._color.local = local;
    }

    /**
     * @return {Number}
     */
    get formulaId() {
        return this._id;
    }

    /**
     * @param {Number} formulaId
     */
    set formulaId(formulaId) {
        this._id = formulaId;
    }

    /**
     * @return {Object}
     */
    get created() {
        return this._created;
    }

    /**
     * @param {Object} created
     */
    set created(created) {
        this._created = created;
    }

    /**
     * @return {Object}
     */
    get updated() {
        return this._updated;
    }

    /**
     * @param {Object} updated
     */
    set updated(updated) {
        this._updated = updated;
    }

    /**
     * @return {Boolean}
     */
    get enabled() {
        return this._enabled;
    }

    /**
     * @param {Boolean} enabled
     */
    set enabled(enabled) {
        this._enabled = enabled;
    }

    /**
     * @return {Boolean}
     */
    get verified() {
        return this._verified;
    }

    /**
     * @param {Boolean} verified
     */
    set verified(verified) {
        this._verified = verified;
    }

    /**
     * @return {Boolean}
     */
    get rejected() {
        return this._rejected;
    }

    /**
     * @param {Boolean} rejected
     */
    set rejected(rejected) {
        this._rejected = rejected;
    }

    /**
     * @return {Boolean}
     */
    get notAvailable() {
        return this._notAvailable;
    }

    /**
     * @param {Boolean} notAvailable
     */
    set notAvailable(notAvailable) {
        this._notAvailable = notAvailable;
    }

    /**
     * @return {Number}
     */
    get statsType() {
        return this._statsType;
    }

    /**
     * @param {Number} statsType
     */
    set statsType(statsType) {
        this._statsType = statsType;
    }

    /**
     * @return {Number}
     */
    get scaling() {
        return this._scaling;
    }

    /**
     * @param {Number} scaling
     */
    set scaling(scaling) {
        this._scaling = scaling;
    }

    /**
     * @return {Boolean}
     */
    get modified() {
        return this._modified;
    }

    /**
     * @param {Boolean} modified
     */
    set modified(modified) {
        this._modified = modified;
    }

    /**
     * @return {Number | null}
     */
    get cifId() {
        return this._cifId;
    }

    /**
     * @param {Number | null} cifId
     */
    set cifId(cifId) {
        this._cifId = cifId;
    }

    /**
     * @return {Number | null}
     */
    get bipId() {
        return this._bipId;
    }

    /**
     * @param {Number | null} bipId
     */
    set bipId(bipId) {
        this._bipId = bipId;
    }

    /**
     * @return {Number | null}
     */
    get formulaSourceId() {
        return this._formulaSourceId;
    }

    /**
     * @param {Number | null} formulaSourceId
     */
    set formulaSourceId(formulaSourceId) {
        this._formulaSourceId = formulaSourceId;
    }

    /**
     * @return {Number | null}
     */
    get formulaTypeId() {
        return this._formulaTypeId;
    }

    /**
     * @param {Number | null} formulaTypeId
     */
    set formulaTypeId(formulaTypeId) {
        this._formulaTypeId = formulaTypeId;
    }

    /**
     * @param {Colorant} colorant
     */
    addColorant(colorant) {
        this._colorantList.push(colorant);
    }

    /**
     * @param {Colorant} colorant
     */
    addColorantG(colorant) {
        this._colorantListG.push(colorant);
    }

    /**
     * @param {Colorant} colorant
     */
    addColorantShot(colorant) {
        this._colorantListShot.push(colorant);
    }

    /**
     * @return {Formula}
     */
    clone() {
        let clone = new Formula(this._id, this._color.clone(), this._colorantList, this._created, this._updated);

        clone.enabled = this._enabled;
        clone.verified = this._verified;
        clone.rejected = this._rejected;
        clone.notAvailable = this._notAvailable;
        clone.statsType = this._statsType;
        clone.cifId = this._cifId;
        clone.bipId = this._bipId;

        clone.colorantListG = this._colorantListG;
        clone.colorantListShot = this._colorantListShot;
        clone.colorantListMachine = this._colorantListMachine;
        clone.scaling = this._scaling;
        clone.modified = this._modified;

        clone._modifiedColorantList = this._modifiedColorantList;
        clone._modifiedColorantListG = this._modifiedColorantListG;
        clone._modifiedColorantListShot = this._modifiedColorantListShot;

        clone.dispensedColorantList = this._dispensedColorantList;
        clone.dispensedColorantListG = this._dispensedColorantListG;
        clone.dispensedColorantListShot = this._dispensedColorantListShot;

        clone._colorantsGroupId = this._colorantsGroupId;
        clone._destinationId = this._destinationId;
        clone._validation = this._validation;
        clone._validationAll = this._validationAll;
        clone._formulaSourceId = this._formulaSourceId;
        clone._formulaCombinationId = this._formulaCombinationId;
        clone._formulaTypeId = this._formulaTypeId;

        return clone;
    }

    /**
     * @return {String}
     */
    toString() {
        return "Formula (" + this._id + ")";
    }

    /**
     * Returns array of colorants in structure for validation, dispensing and calculation of manual formula in RCC.
     *
     * @return {Array}
     */
    getCorrectionVolumes = () => {
        let result = [];

        let colorant;
        for (colorant of this.realColorantList) {
            if (colorant.correction > 0) {
                result.push({
                    cntCode: colorant.name,
                    volume: colorant.correction,
                    dispType: colorant.dispensingLogic
                });
            }
        }

        return result;
    };

    /**
     * Returns array of colorants in structure for validation, dispensing and calculation of manual formula in RCC.
     *
     * @return {Array}
     */
    getColorantVolumes = () => {
        if (this.isCorrection()) {
            return this.getCorrectionVolumes();
        }

        let result = [];

        let colorant;
        for (colorant of this.realColorantList) {
            result.push({
                cntCode: colorant.name,
                volume: colorant.realAmount,
                dispType: colorant.dispensingLogic
            });
        }

        return result;
    };

    /**
     * Returns array of colorant amounts in structure for requests such as recalculate standard formula.
     *
     * @return {Array}
     */
    getColorantAmounts = () => {
        let result = {};

        let colorant;
        for (colorant of this._colorantList) {
            result[colorant.name] = colorant.realAmount;
        }

        return result;
    };

    /**
     * Returns array of colorant amounts in structure for requests such as add custom formula, formula meta data,
     * recalculate modified formula, add order record and add statistics record.
     *
     * @return {Array}
     */
    getRealColorantAmounts = () => {
        let result = {};

        let colorant;
        for (colorant of this.realColorantList) {
            result[colorant.name] = colorant.realAmount;
        }

        return result;
    };

    /**
     * Returns array of colorant correction amounts in structure for requests such as add statistics correction.
     *
     * @return {Array}
     */
    getCorrectionAmounts = () => {
        let result = {};

        let colorant;
        for (colorant of this.realColorantList) {
            if (colorant.correction > 0) {
                result[colorant.name] = colorant.correction;
            }
        }

        return result;
    };

    /**
     * Returns first colorant with warning level.
     *
     * @return {Object}
     */
    getInvalidLevelColorant = () => {
        for (let colorant of this._colorantList) {
            if (
                colorant.validationResult === rcc.RCC_VALIDATION_WARNING_LEVEL ||
                colorant.validationResult === rcc.RCC_VALIDATION_CRITICAL_LEVEL
            ) {
                return colorant;
            }
        }

        return null;
    };

    /**
     * Returns array of colorants with warning level.
     *
     * @return {Array}
     */
    getInvalidLevelColorants = () => {
        let result = [];

        let colorant;
        for (colorant of this._colorantList) {
            if (
                colorant.validationResult === rcc.RCC_VALIDATION_WARNING_LEVEL ||
                colorant.validationResult === rcc.RCC_VALIDATION_CRITICAL_LEVEL
            ) {
                result.push(colorant);
            }
        }
        return result;
    };

    /**
     * Private function to search validationResult in array of results.
     *
     * @param   {Array}     validationResults
     * @param   {Object}    colorant
     * @return  {Object}
     */
    getValidationResult = (validationResults, colorant) => {
        let newItem = validationResults.find((result) => result.cntCode === colorant.name);
        if (newItem !== undefined && newItem.canister !== null) {
            switch (newItem.validation) {
                case rcc.RCC_VALIDATION_MANUAL_COLORANT_ADDITION:
                case rcc.RCC_VALIDATION_MANUAL_COLORANT_ADDITION_CUSTOM_CAN:
                    if (newItem.canister.currentLevel - newItem.volume < newItem.canister.minimumLevel) {
                        newItem.validation = rcc.RCC_VALIDATION_CRITICAL_LEVEL_AND_MANUAL;
                        break;
                    }
                    if (newItem.canister.currentLevel - newItem.volume < newItem.canister.warningLevel) {
                        newItem.validation = rcc.RCC_VALIDATION_WARNING_LEVEL_AND_MANUAL;
                        break;
                    }
                    break;
                case rcc.RCC_VALIDATION_SPLIT_FORMULA:
                    if (newItem.canister.currentLevel - newItem.volume < newItem.canister.minimumLevel) {
                        newItem.validation = rcc.RCC_VALIDATION_CRITICAL_LEVEL_AND_SPLIT;
                        break;
                    }
                    if (newItem.canister.currentLevel - newItem.volume < newItem.canister.warningLevel) {
                        newItem.validation = rcc.RCC_VALIDATION_WARNING_LEVEL_AND_SPLIT;
                        break;
                    }
                    break;
                default:
                    break;
            }
        }
        return newItem;
    };

    /**
     * Private function to set validationResult to given colorant list.
     *
     * @param   {Array}     colorantList
     * @param   {Array}     validationResults
     * @return  {Array}
     */
    applyValidationResult = (colorantList, validationResults) => {
        if (colorantList.length === 0) {
            return [];
        }

        let colorant, result;
        let newColorants = [];
        for (colorant of colorantList) {
            result = this.getValidationResult(validationResults[1], colorant);
            if (result !== undefined) {
                newColorants.push(colorant.updateValidationResult(result.validation));
            } else {
                newColorants.push(colorant);
            }
        }

        return newColorants;
    };

    /**
     * Clones formula object and updates colorant validation results.
     *
     * @param   {Array}     validation
     * @return  {Object}
     */
    validateColorants = (validation) => {
        let formula = this.clone();

        formula.colorantList = this.applyValidationResult(this._colorantList, validation);
        formula.colorantListG = this.applyValidationResult(this._colorantListG, validation);
        formula.colorantListShot = this.applyValidationResult(this._colorantListShot, validation);
        formula.colorantListMachine = this.applyValidationResult(this._colorantListMachine, validation);
        formula._modifiedColorantList = this.applyValidationResult(this._modifiedColorantList, validation);
        formula._modifiedColorantListG = this.applyValidationResult(this._modifiedColorantListG, validation);
        formula._modifiedColorantListShot = this.applyValidationResult(this._modifiedColorantListShot, validation);

        formula._validation = validation[0];
        formula._validationAll = validation[2];

        return formula;
    };

    /**
     * Clones formula object and updates color name property.
     *
     * @param   {String} name
     * @return  {Object}
     */
    updateName = (name) => {
        let newFormula = this.clone();
        newFormula.color.name = name;

        return newFormula;
    };

    /**
     * Clones formula object and updates color code property.
     *
     * @param   {String} code
     * @return  {Object}
     */
    updateCode = (code) => {
        let newFormula = this.clone();
        newFormula.color.code = code;

        return newFormula;
    };

    /**
     * Clones formula object and updates color rgb property.
     *
     * @param   {String} rgb
     * @return  {Object}
     */
    updateRgb = (rgb) => {
        let newFormula = this.clone();
        newFormula.color.rgb = rgb;

        return newFormula;
    };

    /**
     * Clones formula object and updates color code and rgb property.
     *
     * @param   {String} code
     * @param   {String} rgb
     * @return  {Object}
     */
    updateCodeAndRgb = (code, rgb) => {
        let newFormula = this.clone();
        newFormula.color.rgb = rgb;

        if (code !== null) {
            newFormula.color.code = code;
        }

        return newFormula;
    };

    /**
     * Clones formula object and updates custom formula properties.
     *
     * @param   {Boolean}   automaticNaming
     * @param   {String}    unit
     * @return  {Object}
     */
    convertToCustom = (automaticNaming, unit) => {
        let newFormula = this.clone();
        newFormula.id = 0;
        newFormula.color.id = 0;
        newFormula.color.local = true;

        if (automaticNaming) {
            newFormula.color.name = newFormula.color.code = this.getAutomaticName(unit);
        } else {
            newFormula.color.name = this._color.displayName;
        }

        return newFormula;
    };

    /**
     * Clones formula object and updates stats type property.
     *
     * @param   {Number}    statsType
     * @return  {Object}
     */
    updateStatsType = (statsType) => {
        let newFormula = this.clone();
        newFormula.statsType = statsType;

        return newFormula;
    };

    /**
     * Clones formula object and updates colorant amount properties.
     *
     * @param {Array}   colorantList
     * @param {Array}   colorantListG
     * @param {Array}   colorantListShot
     * @return {Object}
     */
    updateColorantAmounts = (colorantList, colorantListG, colorantListShot) => {
        let newFormula = this.clone();

        newFormula.colorantList = colorantList;
        newFormula.colorantListG = colorantListG;
        newFormula.colorantListShot = colorantListShot;

        return newFormula;
    };

    /**
     * Updates modified colorant amount properties.
     *
     * @param {Array}   colorantList
     * @param {Array}   colorantListG
     * @param {Array}   colorantListShot
     */
    updateModifiedColorantLists = (colorantList, colorantListG, colorantListShot) => {
        this._modifiedColorantList = colorantList;
        this._modifiedColorantListG = colorantListG;
        this._modifiedColorantListShot = colorantListShot;
    };

    /**
     * Clones formula object and updates modified colorant amount properties.
     *
     * @param {Array}   colorantList
     * @param {Array}   colorantListG
     * @param {Array}   colorantListShot
     * @return {Object}
     */
    updateModifiedColorantAmounts = (colorantList, colorantListG, colorantListShot) => {
        let newFormula = this.clone();

        newFormula.updateModifiedColorantLists(colorantList, colorantListG, colorantListShot);

        let colorant = null;
        let correctionSum = 0;
        for (colorant of newFormula._modifiedColorantList) {
            colorant.correction = colorant.amount - modelHelper.getAmountByColorant(newFormula._colorantList, colorant);

            if (Math.abs(colorant.correction) < 0.00001) {
                colorant.correction = 0;
            }

            correctionSum += colorant.correction;
        }

        for (colorant of newFormula._modifiedColorantListG) {
            colorant.correction =
                colorant.amount - modelHelper.getAmountByColorant(newFormula._colorantListG, colorant);

            if (Math.abs(colorant.correction) < 0.00001) {
                colorant.correction = 0;
            }

            correctionSum += colorant.correction;
        }

        for (colorant of newFormula._modifiedColorantListShot) {
            colorant.correction =
                colorant.amount - modelHelper.getAmountByColorant(newFormula._colorantListShot, colorant);

            if (Math.abs(colorant.correction) < 0.00001) {
                colorant.correction = 0;
            }

            correctionSum += colorant.correction;
        }

        if (correctionSum === 0) {
            newFormula.modified = false;
        }

        return newFormula;
    };

    /**
     * Returns colorant by ID from modified colorant list.
     *
     * @return {Boolean}
     */
    findModifiedColorantList = (id) => {
        let colorant;
        for (colorant of this._modifiedColorantList) {
            if (colorant.id === id) {
                return colorant;
            }
        }

        return null;
    };

    /**
     * Returns true if colorant list if different as modified colorant list.
     *
     * @return {Boolean}
     */
    isModified = () => {
        if (this._colorantList.length !== this._modifiedColorantList.length) {
            return true;
        }

        let colorant, modifiedColorant;
        for (colorant of this._colorantList) {
            modifiedColorant = this.findModifiedColorantList(colorant.id);

            if (modifiedColorant === null || modifiedColorant.amount !== colorant.amount) {
                return true;
            }
        }

        return false;
    };

    /**
     * Sets corrections.
     */
    setCorrections = () => {
        let colorant = null;
        for (colorant of this._modifiedColorantList) {
            colorant.correction = colorant.amount - modelHelper.getAmountByColorant(this._colorantList, colorant);
        }
        for (colorant of this._modifiedColorantListG) {
            colorant.correction = colorant.amount - modelHelper.getAmountByColorant(this._colorantListG, colorant);
        }
        for (colorant of this._modifiedColorantListShot) {
            colorant.correction = colorant.amount - modelHelper.getAmountByColorant(this._colorantListShot, colorant);
        }
    };

    /**
     * Clones formula object and modifies colorant properties.
     *
     * @param {Array}   colorantList
     * @param {Array}   colorantListG
     * @param {Array}   colorantListShot
     * @param {Boolean} skipCorrections
     * @return {Object}
     */
    modifyFormula = (colorantList, colorantListG, colorantListShot, skipCorrections = false) => {
        let newFormula = this.clone();

        newFormula._modifiedColorantList = colorantList;
        newFormula._modifiedColorantListG = colorantListG;
        newFormula._modifiedColorantListShot = colorantListShot;

        if (!skipCorrections) {
            newFormula.setCorrections();
        }

        newFormula.scaling = 1;
        newFormula.modified = newFormula.isModified();

        return newFormula;
    };

    /**
     * Clones formula object and updates colorantListG by colorantListShot.
     *
     * @return {Object}
     */
    updateColorantsByMl = (shotsCoef, shotsName = settings.UNIT_SHOT) => {
        let formula = this.clone();

        formula.colorantListG = conversionHelper.convertColorantList(
            this._colorantList,
            settings.UNIT_ML,
            settings.UNIT_G
        );

        formula.colorantListShot = conversionHelper.convertColorantList(
            this._colorantList,
            settings.UNIT_ML,
            settings.UNIT_SHOT,
            shotsCoef,
            shotsName
        );

        return formula;
    };

    /**
     * Clones formula object and updates colorantList by colorantListShot.
     *
     * @return {Object}
     */
    updateColorantsMlByShots = (shotsCoef) => {
        let formula = this.clone();

        formula.colorantList = conversionHelper.convertColorantList(
            this._colorantListShot,
            settings.UNIT_SHOT,
            settings.UNIT_ML,
            shotsCoef
        );

        formula.colorantListG = conversionHelper.convertColorantList(
            formula.colorantList,
            settings.UNIT_ML,
            settings.UNIT_G
        );

        return formula;
    };

    /**
     * Clones formula object and updates colorantList by colorantListG.
     *
     * @return {Object}
     */
    updateColorantsMlByG = (shotsCoef) => {
        let formula = this.clone();

        formula.colorantList = conversionHelper.convertColorantList(
            this._colorantListG,
            settings.UNIT_G,
            settings.UNIT_ML
        );

        formula.colorantListShot = conversionHelper.convertColorantList(
            formula.colorantList,
            settings.UNIT_ML,
            settings.UNIT_SHOT,
            shotsCoef
        );

        return formula;
    };

    /**
     * Clones formula object and calculates all colorant amount properties from given list and unit.
     *
     * @param   {Array}     colorantList
     * @param   {String}    source
     * @param   {String}    shotsCoef
     * @param   {String}    shotsName
     * @return  {Object}
     */
    editColorantList = (colorantList, source, shotsCoef, shotsName) => {
        let correctedColorantList = [];

        if (source === settings.UNIT_G) {
            for (let colorant of colorantList) {
                correctedColorantList.push(
                    colorant.updateCorrection(
                        colorant.amount - modelHelper.getAmountByColorant(this._colorantListG, colorant)
                    )
                );
            }

            return this.modifyFormula(
                conversionHelper.convertEditedColorantList(
                    correctedColorantList,
                    source,
                    settings.UNIT_ML,
                    shotsCoef,
                    shotsName,
                    this._colorantList
                ),
                correctedColorantList,
                conversionHelper.convertEditedColorantList(
                    correctedColorantList,
                    source,
                    settings.UNIT_SHOT,
                    shotsCoef,
                    shotsName,
                    this._colorantListShot
                )
            );
        }

        if (source === settings.UNIT_SHOT) {
            for (let colorant of colorantList) {
                correctedColorantList.push(
                    colorant.updateCorrection(
                        colorant.amount - modelHelper.getAmountByColorant(this._colorantListShot, colorant)
                    )
                );
            }

            return this.modifyFormula(
                conversionHelper.convertEditedColorantList(
                    correctedColorantList,
                    source,
                    settings.UNIT_ML,
                    shotsCoef,
                    shotsName,
                    this._colorantList
                ),
                conversionHelper.convertEditedColorantList(
                    correctedColorantList,
                    source,
                    settings.UNIT_G,
                    shotsCoef,
                    shotsName,
                    this._colorantListG
                ),
                correctedColorantList
            );
        }

        for (let colorant of colorantList) {
            correctedColorantList.push(
                colorant.updateCorrection(
                    colorant.amount - modelHelper.getAmountByColorant(this._colorantList, colorant)
                )
            );
        }

        return this.modifyFormula(
            correctedColorantList,
            conversionHelper.convertEditedColorantList(
                correctedColorantList,
                source,
                settings.UNIT_G,
                shotsCoef,
                shotsName,
                this._colorantListG
            ),
            conversionHelper.convertEditedColorantList(
                correctedColorantList,
                source,
                settings.UNIT_SHOT,
                shotsCoef,
                shotsName,
                this._colorantListShot
            )
        );
    };

    /**
     * Clones formula object and calculates colorant amounts for grams and shots.
     *
     * @param   {Array}     colorantList
     * @param   {String}    shotsCoef
     * @param   {String}    shotsName
     * @return  {Object}
     */
    editColorantListWithCorrection = (colorantList, shotsCoef, shotsName) => {
        return this.modifyFormula(
            colorantList,
            conversionHelper.convertEditedColorantList(
                colorantList,
                settings.UNIT_ML,
                settings.UNIT_G,
                shotsCoef,
                shotsName,
                this._colorantListG
            ),
            conversionHelper.convertEditedColorantList(
                colorantList,
                settings.UNIT_ML,
                settings.UNIT_SHOT,
                shotsCoef,
                shotsName,
                this._colorantListShot
            ),
            true
        );
    };

    /**
     * Reset scaled formula colorant lists.
     */
    resetScaling = () => {
        this._modifiedColorantList = [];
        this._modifiedColorantListG = [];
        this._modifiedColorantListShot = [];

        this._scaling = 1;
        this._modified = false;
    };

    /**
     * Scale formula colorant lists.
     *
     * @param   {Number}    scaling
     */
    scale = (scaling) => {
        if (scaling !== 1) {
            this._modifiedColorantList = modelHelper.scaleColorantList(this._colorantList, scaling);
            this._modifiedColorantListG = modelHelper.scaleColorantList(this._colorantListG, scaling);
            this._modifiedColorantListShot = modelHelper.scaleColorantList(this._colorantListShot, scaling);

            let colorant = null;
            for (colorant of this._modifiedColorantList) {
                colorant.correction = colorant.amount - modelHelper.getAmountByColorant(this._colorantList, colorant);
            }
            for (colorant of this._modifiedColorantListG) {
                colorant.correction = colorant.amount - modelHelper.getAmountByColorant(this._colorantListG, colorant);
            }
            for (colorant of this._modifiedColorantListShot) {
                colorant.correction =
                    colorant.amount - modelHelper.getAmountByColorant(this._colorantListShot, colorant);
            }

            this.scaling = scaling;
            this.modified = false;
        } else {
            this.resetScaling();
        }
    };

    /**
     * Clones formula object and scale formula colorant lists.
     *
     * @param   {Number}    scaling
     * @return  {Object}
     */
    scaleFormula = (scaling) => {
        let newFormula = this.clone();

        newFormula.scale(scaling);

        return newFormula;
    };

    /**
     * Sets scaling of formula and calculates modified colorant lists.
     *
     * @param   {Number}    scaling
     */
    setScaling = (scaling) => {
        if (scaling !== 1) {
            this._modifiedColorantList = this._colorantList;
            this._modifiedColorantListG = this._colorantListG;
            this._modifiedColorantListShot = this._colorantListShot;
            this.colorantList = modelHelper.unscaleColorantList(this._colorantList, scaling);
            this.colorantListG = modelHelper.unscaleColorantList(this._colorantListG, scaling);
            this.colorantListShot = modelHelper.unscaleColorantList(this._colorantListShot, scaling);

            let colorant = null;
            for (colorant of this._modifiedColorantList) {
                colorant.correction = colorant.amount - modelHelper.getAmountByColorant(this._colorantList, colorant);
            }
            for (colorant of this._modifiedColorantListG) {
                colorant.correction = colorant.amount - modelHelper.getAmountByColorant(this._colorantListG, colorant);
            }
            for (colorant of this._modifiedColorantListShot) {
                colorant.correction =
                    colorant.amount - modelHelper.getAmountByColorant(this._colorantListShot, colorant);
            }

            this._scaling = scaling;
            this._modified = false;
        } else {
            this.resetScaling();
        }
    };

    /**
     * Clones formula object and sets formula colorant lists before editing.
     *
     * @return  {Object}
     */
    updateFormulaBeforeEditing = () => {
        if (!this.isManualCorrection()) {
            return this;
        }

        let newFormula = this.clone();
        newFormula.modified = true;

        if (this.modified) {
            newFormula._modifiedColorantList = this._modifiedColorantList.map((colorant) =>
                colorant.updateAmount(colorant.correction)
            );
            newFormula._modifiedColorantListG = this._modifiedColorantListG.map((colorant) =>
                colorant.updateAmount(colorant.correction)
            );
            newFormula._modifiedColorantListShot = this._modifiedColorantListShot.map((colorant) =>
                colorant.updateAmount(colorant.correction)
            );

            return newFormula;
        }

        newFormula._modifiedColorantList = this._colorantList.map((colorant) => colorant.updateAmount(0));
        newFormula._modifiedColorantListG = this._colorantListG.map((colorant) => colorant.updateAmount(0));
        newFormula._modifiedColorantListShot = this._colorantListShot.map((colorant) => colorant.updateAmount(0));

        return newFormula;
    };

    /**
     * Clones formula object and sets formula colorant lists by modified lists.
     *
     * @return  {Object}
     */
    updateFormulaBeforeCorrection = () => {
        if (!this.modified) {
            return this;
        }

        let newFormula = this.clone();
        newFormula.modified = false;
        newFormula._colorantList = this._modifiedColorantList.map((colorant) => colorant.updateAmount(colorant.amount));
        newFormula._colorantListG = this._modifiedColorantListG.map((colorant) =>
            colorant.updateAmount(colorant.amount)
        );
        newFormula._colorantListShot = this._modifiedColorantListShot.map((colorant) =>
            colorant.updateAmount(colorant.amount)
        );
        newFormula._modifiedColorantList = [];
        newFormula._modifiedColorantListG = [];
        newFormula._modifiedColorantListShot = [];

        return newFormula;
    };

    /**
     * Resets formula modifications.
     *
     * @param   {Object | null} color
     */
    resetModifications = (color) => {
        let newFormula = this.clone();
        newFormula.resetScaling();

        if (color !== null) {
            newFormula.color.rgb = color.rgb;
        }

        return newFormula;
    };

    getFormulaUnit = (formulaUnit) => {
        if (formulaUnit === settings.UNIT_SHOT && this.colorantListShot.length > 0) {
            return this.colorantListShot[0].amountType;
        }

        return formulaUnit;
    };

    getPrintData = (formulaUnit, activeFormulaUnit = null) => {
        let colorantList = this.modified
            ? this.getColorantListDifferences(formulaUnit)
            : this.getRealColorantListByUnit(formulaUnit);

        let nameList = [];
        let amountList = [];
        let recipeList = [];
        let correctionList = [];
        let correctionRecipeList = [];

        let item;
        for (item of colorantList) {
            nameList.push(item.name);
            amountList.push(item.formatAmount());
            recipeList.push(item.name + "=" + item.formatAmount());
            correctionList.push(item.formatCorrectionForPrintData());
            correctionRecipeList.push(item.name + "=" + item.formatCorrectionForPrintData());
        }

        let printData = {
            colorants: nameList.join("\n"),
            colorantsinline: nameList.join(" | "),
            formulaunit: this.getFormulaUnit(formulaUnit),
            formula: amountList.join("\n"),
            formulainline: amountList.join(" | "),
            recipe: recipeList.join("\n"),
            recipeinline: recipeList.join(" "),
            corrections: correctionList.join("\n"),
            correctionsinline: correctionList.join(" | "),
            correctionrecipe: correctionRecipeList.join("\n"),
            correctionrecipeinline: correctionRecipeList.join(" | ")
        };

        for (let unit of [settings.UNIT_ML, settings.UNIT_G, settings.UNIT_SHOT, settings.UNIT_MANUAL]) {
            if (unit === formulaUnit) {
                printData["corrections_" + unit] = printData.corrections;
                printData["correctionsinline_" + unit] = printData.correctionsinline;
                printData["formula_" + unit] = printData.formula;
                printData["formulainline_" + unit] = printData.formulainline;
                printData["recipe_" + unit] = printData.recipe;
                printData["recipeinline_" + unit] = printData.recipeinline;
                printData["correctionrecipe_" + unit] = printData.correctionrecipe;
                printData["correctionrecipeinline_" + unit] = printData.correctionrecipeinline;
            } else {
                amountList = [];
                correctionList = [];
                recipeList = [];
                correctionRecipeList = [];

                for (item of this.getRealColorantListByUnit(unit)) {
                    amountList.push(item.formatAmount());
                    correctionList.push(item.formatCorrectionForPrintData());
                    recipeList.push(item.name + "=" + item.formatAmount());
                    correctionRecipeList.push(item.name + "=" + item.formatCorrectionForPrintData());
                }

                printData["corrections_" + unit] = correctionList.join("\n");
                printData["correctionsinline_" + unit] = correctionList.join(" | ");
                printData["formula_" + unit] = amountList.join("\n");
                printData["formulainline_" + unit] = amountList.join(" | ");
                printData["recipe_" + unit] = recipeList.join("\n");
                printData["recipeinline_" + unit] = recipeList.join(" | ");
                printData["correctionrecipe_" + unit] = correctionRecipeList.join("\n");
                printData["correctionrecipeinline_" + unit] = correctionRecipeList.join(" | ");
            }
        }

        if (activeFormulaUnit === null) {
            activeFormulaUnit = formulaUnit;
        }

        let amountListPump1 = [];
        let amountListPump2 = [];

        for (item of this.colorantListMachine) {
            amountListPump1.push(item.pump1);
            amountListPump2.push(item.pump2);
        }

        printData["formula_manual_pump1"] = amountListPump1.join("\n");
        printData["formula_manual_pump2"] = amountListPump2.join("\n");

        printData["corrections_active"] = printData["corrections_" + activeFormulaUnit];
        printData["correctionsinline_active"] = printData["correctionsinline_" + activeFormulaUnit];
        printData["correctionrecipe_active"] = printData["correctionrecipe_" + activeFormulaUnit];
        printData["correctionrecipeinline_active"] = printData["correctionrecipeinline_" + activeFormulaUnit];
        printData["formula_active"] = printData["formula_" + activeFormulaUnit];
        printData["formulainline_active"] = printData["formulainline_" + activeFormulaUnit];
        printData["recipe_active"] = printData["recipe_" + activeFormulaUnit];
        printData["recipeinline_active"] = printData["recipeinline_" + activeFormulaUnit];
        printData["formulaunit_active"] = this.getFormulaUnit(activeFormulaUnit);
        printData["formula_modified"] = "";

        const statsType = this.getStatsType();

        printData["formula_type"] = translationHelper.getFormulaTypesTranslations(statsType);
        printData["formula_type_id"] = statsType;

        if (
            statsType === statsTypes.MODIFIED_MAIN_FORMULA ||
            statsType === statsTypes.MODIFIED_LOCAL_FORMULA ||
            statsType === statsTypes.AUTOMATIC_CORRECTION ||
            statsType === statsTypes.MODIFIED_AUTOMATIC_CORRECTION ||
            statsType === statsTypes.MANUAL_CORRECTION
        ) {
            printData["corrections_printable"] = true;
        } else {
            printData["corrections_printable"] = false;
        }

        if (statsType === statsTypes.MODIFIED_MAIN_FORMULA || statsType === statsTypes.MODIFIED_LOCAL_FORMULA) {
            printData["formula_modified"] = printData["formula_type"];
        }

        printData["colorant_type"] = translationHelper.getFormulaColorantPropertiesTranslations(this._colorantsGroupId);
        printData["destination_of_use"] = translationHelper.getFormulaDestinationPropertiesTranslations(
            this._destinationId
        );

        return printData;
    };

    /**
     * Clones formula object and modifies colorant list for manual machine property.
     *
     * @param   {Array}     colorantListMachine
     * @return  {Object}
     */
    updateColorantListMachine = (colorantListMachine) => {
        let newFormula = this.clone();

        newFormula._colorantListMachine = colorantListMachine;

        return newFormula;
    };

    /**
     * Clones Formula object and updates dispense logic property of given colorant.
     *
     * @param   {String}    colorantName
     * @param   {Number}    dispenseLogic
     * @return  {Object}
     */
    updateColorantDispenseLogic = (colorantName, dispenseLogic) => {
        let colorantListMl = [];
        let colorantListG = [];
        let colorantListShot = [];
        let modifiedColorantListMl = [];
        let modifiedColorantListG = [];
        let modifiedColorantListShot = [];

        let colorant;
        for (colorant of this.colorantList) {
            if (colorant.name === colorantName) {
                colorantListMl.push(colorant.updateDispensingLogic(dispenseLogic));
            } else {
                colorantListMl.push(colorant);
            }
        }
        for (colorant of this.modifiedColorantList) {
            if (colorant.name === colorantName) {
                modifiedColorantListMl.push(colorant.updateDispensingLogic(dispenseLogic));
            } else {
                modifiedColorantListMl.push(colorant);
            }
        }
        for (colorant of this.colorantListG) {
            if (colorant.name === colorantName) {
                colorantListG.push(colorant.updateDispensingLogic(dispenseLogic));
            } else {
                colorantListG.push(colorant);
            }
        }
        for (colorant of this.modifiedColorantListG) {
            if (colorant.name === colorantName) {
                modifiedColorantListG.push(colorant.updateDispensingLogic(dispenseLogic));
            } else {
                modifiedColorantListG.push(colorant);
            }
        }
        for (colorant of this.colorantListShot) {
            if (colorant.name === colorantName) {
                colorantListShot.push(colorant.updateDispensingLogic(dispenseLogic));
            } else {
                colorantListShot.push(colorant);
            }
        }
        for (colorant of this.modifiedColorantListShot) {
            if (colorant.name === colorantName) {
                modifiedColorantListShot.push(colorant.updateDispensingLogic(dispenseLogic));
            } else {
                modifiedColorantListShot.push(colorant);
            }
        }

        let formula = this.clone();
        formula._colorantList = colorantListMl;
        formula._colorantListG = colorantListG;
        formula._colorantListShot = colorantListShot;
        formula._modifiedColorantList = modifiedColorantListMl;
        formula._modifiedColorantListG = modifiedColorantListG;
        formula._modifiedColorantListShot = modifiedColorantListShot;

        return formula;
    };

    /**
     * Clones Formula object and updates dispense logic property for quick tint.
     *
     * @return  {Object}
     */
    updateColorantDispenseLogicForQuickTint = () => {
        let colorantListMl = [];
        let colorantListG = [];
        let colorantListShot = [];
        let modifiedColorantListMl = [];
        let modifiedColorantListG = [];
        let modifiedColorantListShot = [];

        let colorant;
        for (colorant of this.colorantList) {
            colorantListMl.push(colorant.updateDispensingLogic(rcc.DISPENSING_LOGIC_FORCE_DISPENSING));
        }
        for (colorant of this.modifiedColorantList) {
            modifiedColorantListMl.push(colorant.updateDispensingLogic(rcc.DISPENSING_LOGIC_FORCE_DISPENSING));
        }
        for (colorant of this.colorantListG) {
            colorantListG.push(colorant.updateDispensingLogic(rcc.DISPENSING_LOGIC_FORCE_DISPENSING));
        }
        for (colorant of this.modifiedColorantListG) {
            modifiedColorantListG.push(colorant.updateDispensingLogic(rcc.DISPENSING_LOGIC_FORCE_DISPENSING));
        }
        for (colorant of this.colorantListShot) {
            colorantListShot.push(colorant.updateDispensingLogic(rcc.DISPENSING_LOGIC_FORCE_DISPENSING));
        }
        for (colorant of this.modifiedColorantListShot) {
            modifiedColorantListShot.push(colorant.updateDispensingLogic(rcc.DISPENSING_LOGIC_FORCE_DISPENSING));
        }

        let formula = this.clone();
        formula._colorantList = colorantListMl;
        formula._colorantListG = colorantListG;
        formula._colorantListShot = colorantListShot;
        formula._modifiedColorantList = modifiedColorantListMl;
        formula._modifiedColorantListG = modifiedColorantListG;
        formula._modifiedColorantListShot = modifiedColorantListShot;

        return formula;
    };

    /**
     * @return {String}
     */
    formatCreated() {
        if (this._created !== null) {
            return formatHelper.formatDate(this._created);
        }

        return "";
    }

    /**
     * @return {String}
     */
    formatLastUpdateDate() {
        if (this._updated !== null) {
            return formatHelper.formatDate(this._updated);
        }

        if (this._created !== null) {
            return formatHelper.formatDate(this._created);
        }

        return "";
    }

    /**
     * @return {Number}
     */
    getStatsType() {
        if (this._statsType !== null) {
            return this._statsType;
        }

        if (this._id === config.QUICK_TINT_ID) {
            return statsTypes.QUICK_TINT;
        }

        if (this._color.local) {
            if (this._modified || this._scaling !== 1) {
                return statsTypes.MODIFIED_LOCAL_FORMULA;
            }

            if (this._formulaSourceId === formulaTypes.OWN_FORMULA_SOURCE.MATCHING) {
                return statsTypes.LOCAL_FORMULA_MATCHING;
            }

            return statsTypes.LOCAL_FORMULA;
        }

        if (this._modified || this._scaling !== 1) {
            return statsTypes.MODIFIED_MAIN_FORMULA;
        }

        return statsTypes.MAIN_FORMULA;
    }

    /**
     * @return {Boolean}
     */
    isAvailable() {
        if (this._id !== null) {
            return this._id > 0 || this._id === config.QUICK_TINT_ID;
        }

        return this._cifId !== null && this._bipId !== null;
    }

    /**
     * @return {Boolean}
     */
    isIncomplete() {
        if (this._id !== null) {
            return false;
        }

        return this._cifId === null || this._bipId === null;
    }

    /**
     * @return {Boolean}
     */
    isIncompleteOrQuickTint() {
        if (this._id !== null) {
            return this._id === config.QUICK_TINT_ID;
        }

        return this._cifId === null || this._bipId === null;
    }

    /**
     * @return {Boolean}
     */
    isManualCorrection() {
        return this._statsType === statsTypes.MANUAL_CORRECTION;
    }

    /**
     * @return {Boolean}
     */
    isCorrection() {
        return (
            this._statsType === statsTypes.AUTOMATIC_CORRECTION ||
            this._statsType === statsTypes.MODIFIED_AUTOMATIC_CORRECTION ||
            this._statsType === statsTypes.MANUAL_CORRECTION ||
            this._statsType === statsTypes.FINISHED_CORRECTION
        );
    }

    /**
     * @return {Boolean}
     */
    isCorrectionEmpty() {
        return this._modifiedColorantList.every((colorant) => colorant.isCorrectionEmpty());
    }

    /**
     * @param   {Number}    minimalDispenseAmount
     * @return  {Boolean}
     */
    isCorrectionDoseable(minimalDispenseAmount) {
        return this._modifiedColorantList.some((colorant) => colorant.correction >= minimalDispenseAmount);
    }

    /**
     * @return {Boolean}
     */
    isReformulation() {
        return this._statsType === statsTypes.REFORMULATION;
    }

    getColorantListByUnit = (formulaUnit) => {
        if (formulaUnit === settings.UNIT_G) {
            return this._colorantListG;
        }

        if (formulaUnit === settings.UNIT_SHOT) {
            return this._colorantListShot;
        }

        if (formulaUnit === settings.UNIT_MANUAL) {
            return this._colorantListMachine;
        }

        return this._colorantList;
    };

    getRealColorantListByUnit = (formulaUnit) => {
        if (formulaUnit === settings.UNIT_G) {
            return this.realColorantListG;
        }

        if (formulaUnit === settings.UNIT_SHOT) {
            return this.realColorantListShot;
        }

        if (formulaUnit === settings.UNIT_MANUAL) {
            return this._colorantListMachine;
        }

        return this.realColorantList;
    };

    getModifiedColorantListByUnit = (formulaUnit) => {
        if (formulaUnit === settings.UNIT_G) {
            return this._modifiedColorantListG;
        }

        if (formulaUnit === settings.UNIT_SHOT) {
            return this._modifiedColorantListShot;
        }

        if (formulaUnit === settings.UNIT_MANUAL) {
            return this._colorantListMachine;
        }

        return this._modifiedColorantList;
    };

    getDispensedColorantListByUnit = (formulaUnit) => {
        if (formulaUnit === settings.UNIT_G) {
            return this.dispensedColorantListG;
        }

        if (formulaUnit === settings.UNIT_SHOT) {
            return this.dispensedColorantListShot;
        }

        if (formulaUnit === settings.UNIT_MANUAL) {
            return this._colorantListMachine;
        }

        return this.dispensedColorantList;
    };

    /**
     * @param   {String}    unit
     * @return  {Array}
     */
    getColorantListDifferences(unit) {
        if (this._modified || this._scaling !== 1) {
            return modelHelper.getColorantListDifferences(
                this.getColorantListByUnit(unit),
                this.getModifiedColorantListByUnit(unit)
            );
        }

        return this.getColorantListByUnit(unit);
    }

    /**
     * @param   {String}    unit
     * @return  {String}
     */
    getAutomaticName(unit) {
        if (this._scaling !== 1) {
            if (this._scaling - 1 > 0) {
                return this._color.name + " +" + formatHelper.formatPercent(this._scaling - 1);
            }

            return this._color.name + " " + formatHelper.formatPercent(this._scaling - 1);
        }

        if (this._modified) {
            let result = "";
            let colorant = null;

            for (colorant of this.getColorantListDifferences(unit)) {
                if (colorant.correction !== 0) {
                    if (result.length) {
                        result += " ";
                    }

                    result += colorant.formatCorrection() + " " + colorant.amountType + " " + colorant.name;
                }
            }

            return this._color.name + " " + result;
        }

        return this._color.name;
    }

    /**
     * Returns true if formula was modified in laboratory.
     *
     * @return {Boolean}
     */
    wasModified = () => {
        if (this._created === null || this._updated === null) {
            return false;
        }

        return this._created.getTime() !== this._updated.getTime();
    };

    /**
     * Returns true if formula contains invalid colorant.
     *
     * @return {Boolean}
     */
    containsInvalidColorants = () => {
        for (let colorant of this.realColorantList) {
            if (colorant instanceof MachineColorant) {
                return true;
            }
        }

        return false;
    };

    /**
     * Returns text with names of invalid colorant.
     *
     * @return {String}
     */
    formatInvalidColorants = () => {
        let result = [];

        for (let colorant of this.realColorantList) {
            if (colorant instanceof MachineColorant) {
                result.push(colorant.displayName);
            }
        }

        return result.join(", ");
    };

    /**
     * Returns true if formula contains invalid dispensed colorant.
     *
     * @return {Boolean}
     */
    containsInvalidDispensedColorants = () => {
        for (let colorant of this.dispensedColorantList) {
            if (colorant instanceof MachineColorant) {
                return true;
            }
        }

        return false;
    };

    /**
     * Returns text with names of invalid dispensed colorant.
     *
     * @return {String}
     */
    formatInvalidDispensedColorants = () => {
        let result = [];

        for (let colorant of this.dispensedColorantList) {
            if (colorant instanceof MachineColorant) {
                result.push(colorant.displayName);
            }
        }

        return result.join(", ");
    };
}
